import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import StickerSelector from '../components/StickerSelector';
import './WellnessPage.css';

const WellnessPage: React.FC = () => {
  const { userProfile } = useTheme();

  // Mood tracking state
  const [moodEntries, setMoodEntries] = useState<Array<{
    id: number;
    date: string;
    mood: number; // 1-10 scale
    emotions: string[];
    activities: string[];
    notes: string;
    stressLevel: number; // 1-10 scale
  }>>([]);


  // Daily quotes state
  const [dailyQuote, setDailyQuote] = useState({
    text: 'Ты сильнее, чем думаешь, и способна на большее, чем можешь представить.',
    author: 'Внутренний голос',
    category: 'мотивация'
  });

  // Gratitude state
  const [gratitudeEntries, setGratitudeEntries] = useState<Array<{
    id: number;
    date: string;
    items: string[];
    reflection: string;
  }>>([]);

  // Stress tracking state
  const [stressEntries, setStressEntries] = useState<Array<{
    id: number;
    date: string;
    level: number; // 1-10
    triggers: string[];
    copingMethods: string[];
    effectiveness: number; // 1-5
  }>>([]);

  const [currentMood, setCurrentMood] = useState(5);
  const [currentEmotions, setCurrentEmotions] = useState<string[]>([]);
  const [currentActivities, setCurrentActivities] = useState<string[]>([]);
  const [showMoodModal, setShowMoodModal] = useState(false);
  const [showGratitudeModal, setShowGratitudeModal] = useState(false);
  const [showStressModal, setShowStressModal] = useState(false);

  // Balance Wheel state
  const [balanceWheel, setBalanceWheel] = useState({
    career: 5,
    health: 5,
    relationships: 5,
    finances: 5,
    personalGrowth: 5,
    recreation: 5,
    spirituality: 5,
    contribution: 5
  });

  // Vision Board state
  const [visionBoard, setVisionBoard] = useState<Array<{id: number, image: string, caption: string, category: string, uploadedAt: string}>>([]);
  const [showVisionBoardModal, setShowVisionBoardModal] = useState(false);
  const [newVisionItem, setNewVisionItem] = useState({caption: '', category: ''});

  // Load daily quote
  useEffect(() => {
    const quotes = [
      { text: 'Ты сильнее, чем думаешь, и способна на большее, чем можешь представить.', author: 'Внутренний голос', category: 'мотивация' },
      { text: 'Каждый день — это новая возможность стать лучшей версией себя.', author: 'Мудрость', category: 'рост' },
      { text: 'Твоя красота не в совершенстве, а в уникальности.', author: 'Самооценка', category: 'красота' },
      { text: 'Заботься о себе, как заботишься о других.', author: 'Любовь к себе', category: 'забота' },
      { text: 'Маленькие шаги каждый день ведут к большим изменениям.', author: 'Прогресс', category: 'развитие' },
      { text: 'Твои мечты достойны того, чтобы за них бороться.', author: 'Мечты', category: 'цели' }
    ];
    const randomQuote = quotes[Math.floor(Math.random() * quotes.length)];
    setDailyQuote(randomQuote);
  }, []);

  // Mood tracking functions
  const saveMoodEntry = () => {
    const entry = {
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      mood: currentMood,
      emotions: currentEmotions,
      activities: currentActivities,
      notes: '',
      stressLevel: 5
    };
    setMoodEntries([entry, ...moodEntries]);
    setCurrentEmotions([]);
    setCurrentActivities([]);
    setShowMoodModal(false);
  };


  // Gratitude functions
  const saveGratitudeEntry = (items: string[], reflection: string) => {
    const entry = {
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      items,
      reflection
    };
    setGratitudeEntries([entry, ...gratitudeEntries]);
    setShowGratitudeModal(false);
  };

  // Stress tracking functions
  const saveStressEntry = (level: number, triggers: string[], copingMethods: string[], effectiveness: number) => {
    const entry = {
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      level,
      triggers,
      copingMethods,
      effectiveness
    };
    setStressEntries([entry, ...stressEntries]);
    setShowStressModal(false);
  };

  // Balance Wheel functions
  const updateBalanceWheel = (category: string, value: number) => {
    setBalanceWheel(prev => ({
      ...prev,
      [category]: value
    }));
  };

  // Vision Board functions
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target?.result as string;
        const visionItem = {
          id: Date.now(),
          image: imageData,
          caption: newVisionItem.caption,
          category: newVisionItem.category,
          uploadedAt: new Date().toISOString()
        };
        setVisionBoard([...visionBoard, visionItem]);
        setNewVisionItem({caption: '', category: ''});
        setShowVisionBoardModal(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const downloadVisionBoard = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size for phone wallpaper (9:16 ratio)
    canvas.width = 1080;
    canvas.height = 1920;

    // Fill background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Create a grid layout for images
    const cols = 2;
    const rows = Math.ceil(visionBoard.length / cols);
    const cellWidth = canvas.width / cols;
    const cellHeight = canvas.height / rows;

    let imageIndex = 0;
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols && imageIndex < visionBoard.length; col++) {
        const item = visionBoard[imageIndex];
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, col * cellWidth, row * cellHeight, cellWidth, cellHeight);
          
          // Add caption if exists
          if (item.caption) {
            ctx.fillStyle = '#000000';
            ctx.font = '24px Arial';
            ctx.textAlign = 'center';
            ctx.fillText(item.caption, col * cellWidth + cellWidth / 2, row * cellHeight + cellHeight - 20);
          }
        };
        img.src = item.image;
        imageIndex++;
      }
    }

    // Download the image
    setTimeout(() => {
      const link = document.createElement('a');
      link.download = 'vision-board.png';
      link.href = canvas.toDataURL();
      link.click();
    }, 1000);
  };

  // Get today's mood
  const todayMood = moodEntries.find(entry => entry.date === new Date().toISOString().split('T')[0]);

  return (
    <div className="wellness-page">
      <div className="page">
        <div className="page-header">
          <h1 className="page-title animate-fade-in-up">💚 Благополучие и забота о себе</h1>
          <p className="page-subtitle animate-fade-in-up">Твой путь к гармонии и внутреннему спокойствию</p>
        </div>

        {/* Daily Quote */}
        <div className="daily-quote-section glass-card animate-fade-in-up">
          <div className="quote-content">
            <h3>💫 Цитата дня</h3>
            <blockquote className="quote-text">"{dailyQuote.text}"</blockquote>
            <cite className="quote-author">— {dailyQuote.author}</cite>
          </div>
        </div>


        {/* Mood Tracking */}
        <div className="wellness-section">
          <div className="section-header">
            <h3>😊 Дневник настроения</h3>
            <button 
              onClick={() => setShowMoodModal(true)}
              className="btn btn-primary hover-scale"
            >
              Записать настроение
            </button>
          </div>
          
          <div className="mood-tracker">
            {todayMood ? (
              <div className="today-mood glass-card">
                <h4>Сегодняшнее настроение</h4>
                <div className="mood-display">
                  <div className="mood-scale">
                    {[...Array(10)].map((_, i) => (
                      <div 
                        key={i} 
                        className={`mood-point ${todayMood.mood > i ? 'filled' : ''}`}
                      >
                        {i + 1}
                      </div>
                    ))}
                  </div>
                  <div className="mood-emotions">
                    {todayMood.emotions.map((emotion, index) => (
                      <span key={index} className="emotion-tag">{emotion}</span>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <p>Запиши свое настроение сегодня</p>
              </div>
            )}
          </div>
        </div>


        {/* Gratitude Journal */}
        <div className="wellness-section">
          <div className="section-header">
            <h3>🙏 Дневник благодарности</h3>
            <button 
              onClick={() => setShowGratitudeModal(true)}
              className="btn btn-primary hover-scale"
            >
              Записать благодарность
            </button>
          </div>
          
          <div className="gratitude-preview">
            {gratitudeEntries.slice(0, 3).map(entry => (
              <div key={entry.id} className="gratitude-entry glass-card animate-fade-in-up">
                <div className="gratitude-date">{entry.date}</div>
                <div className="gratitude-items">
                  {entry.items.map((item, index) => (
                    <span key={index} className="gratitude-item">✨ {item}</span>
                  ))}
                </div>
                {entry.reflection && (
                  <div className="gratitude-reflection">{entry.reflection}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Stress Tracker */}
        <div className="wellness-section">
          <div className="section-header">
            <h3>😌 Трекер стресса</h3>
            <button 
              onClick={() => setShowStressModal(true)}
              className="btn btn-secondary hover-scale"
            >
              Записать уровень стресса
            </button>
          </div>
          
          <div className="stress-insights">
            <div className="stress-chart glass-card">
              <h4>Уровень стресса за неделю</h4>
              <div className="stress-bars">
                {stressEntries.slice(0, 7).map(entry => (
                  <div key={entry.id} className="stress-bar">
                    <div 
                      className="stress-fill" 
                      style={{ height: `${(entry.level / 10) * 100}%` }}
                    ></div>
                    <div className="stress-label">{entry.level}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Balance Wheel */}
        <div className="wellness-section">
          <div className="section-header">
            <h3>⚖️ Колесо баланса жизни</h3>
          </div>
          
          <div className="balance-wheel-container">
            <div className="balance-wheel">
              <svg width="400" height="400" viewBox="0 0 400 400">
                {/* Background circles */}
                <circle cx="200" cy="200" r="180" fill="none" stroke="#e2e8f0" strokeWidth="2"/>
                <circle cx="200" cy="200" r="140" fill="none" stroke="#e2e8f0" strokeWidth="2"/>
                <circle cx="200" cy="200" r="100" fill="none" stroke="#e2e8f0" strokeWidth="2"/>
                <circle cx="200" cy="200" r="60" fill="none" stroke="#e2e8f0" strokeWidth="2"/>
                <circle cx="200" cy="200" r="20" fill="none" stroke="#e2e8f0" strokeWidth="2"/>
                
                {/* Balance areas */}
                {Object.entries(balanceWheel).map(([category, value], index) => {
                  const angle = (index * 45) * Math.PI / 180;
                  const radius = (value / 10) * 160;
                  const x = 200 + radius * Math.cos(angle - Math.PI / 2);
                  const y = 200 + radius * Math.sin(angle - Math.PI / 2);
                  
                  return (
                    <circle
                      key={category}
                      cx={x}
                      cy={y}
                      r="8"
                      fill="#86B05D"
                      stroke="#ffffff"
                      strokeWidth="2"
                    />
                  );
                })}
                
                {/* Connecting lines */}
                {Object.entries(balanceWheel).map(([category, value], index) => {
                  const angle = (index * 45) * Math.PI / 180;
                  const radius = (value / 10) * 160;
                  const x = 200 + radius * Math.cos(angle - Math.PI / 2);
                  const y = 200 + radius * Math.sin(angle - Math.PI / 2);
                  
                  return (
                    <line
                      key={`line-${category}`}
                      x1="200"
                      y1="200"
                      x2={x}
                      y2={y}
                      stroke="#86B05D"
                      strokeWidth="2"
                      opacity="0.3"
                    />
                  );
                })}
              </svg>
              
              {/* Category labels */}
              <div className="balance-labels">
                {Object.entries(balanceWheel).map(([category, value], index) => {
                  const labels = {
                    career: 'Карьера',
                    health: 'Здоровье',
                    relationships: 'Отношения',
                    finances: 'Финансы',
                    personalGrowth: 'Личный рост',
                    recreation: 'Отдых',
                    spirituality: 'Духовность',
                    contribution: 'Вклад в общество'
                  };
                  
                  return (
                    <div key={category} className="balance-label">
                      <span className="balance-category">{labels[category as keyof typeof labels]}</span>
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={value}
                        onChange={(e) => updateBalanceWheel(category, parseInt(e.target.value))}
                        className="balance-slider"
                      />
                      <span className="balance-value">{value}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Vision Board */}
        <div className="wellness-section">
          <div className="section-header">
            <h3>🎨 Карта желаний</h3>
            <button onClick={() => setShowVisionBoardModal(true)} className="btn btn-primary hover-scale">Добавить изображение</button>
          </div>
          
          <div className="vision-board-container">
            {visionBoard.length === 0 ? (
              <div className="empty-state">
                <p>Создайте свою карту желаний с вдохновляющими изображениями</p>
              </div>
            ) : (
              <div className="vision-board-grid">
                {visionBoard.map(item => (
                  <div key={item.id} className="vision-board-item">
                    <img src={item.image} alt={item.caption} className="vision-image" />
                    {item.caption && (
                      <div className="vision-caption">{item.caption}</div>
                    )}
                    {item.category && (
                      <div className="vision-category">{item.category}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {visionBoard.length > 0 && (
              <div className="vision-board-actions">
                <button onClick={downloadVisionBoard} className="btn btn-secondary hover-scale">
                  📱 Скачать как обои
                </button>
                <p className="vision-board-note">
                  Готовое изображение можно использовать как обои для телефона
                </p>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Progress Overview - Dynamic */}
      <div className="wellness-stats">
        <div className="stat-card hover-lift">
          <div className="stat-icon">😊</div>
          <div className="stat-info">
            <div className="stat-value">{todayMood ? todayMood.mood : '—'}</div>
            <div className="stat-label">Настроение сегодня</div>
          </div>
        </div>
        
        <div className="stat-card hover-lift">
          <div className="stat-icon">🙏</div>
          <div className="stat-info">
            <div className="stat-value">{gratitudeEntries.length}</div>
            <div className="stat-label">Дней благодарности</div>
          </div>
        </div>
        
        <div className="stat-card hover-lift">
          <div className="stat-icon">💪</div>
          <div className="stat-info">
            <div className="stat-value">{stressEntries.length}</div>
            <div className="stat-label">Записей о стрессе</div>
          </div>
        </div>
        
        <div className="stat-card hover-lift">
          <div className="stat-icon">⚖️</div>
          <div className="stat-info">
            <div className="stat-value">{Object.values(balanceWheel).reduce((sum, val) => sum + val, 0)}</div>
            <div className="stat-label">Баланс жизни</div>
          </div>
        </div>
        
        <div className="stat-card hover-lift">
          <div className="stat-icon">🎨</div>
          <div className="stat-info">
            <div className="stat-value">{visionBoard.length}</div>
            <div className="stat-label">Изображений на карте желаний</div>
          </div>
        </div>
      </div>

      {/* Vision Board Modal */}
      <VisionBoardModal
        isOpen={showVisionBoardModal}
        onClose={() => setShowVisionBoardModal(false)}
        onUpload={handleImageUpload}
        newItem={newVisionItem}
        setNewItem={setNewVisionItem}
      />

      {/* Mood Modal */}
      {showMoodModal && (
        <div className="modal-overlay" onClick={() => setShowMoodModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Как ты себя чувствуешь?</h3>
              <button onClick={() => setShowMoodModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <div className="mood-scale-selector">
                <label>Настроение (1-10):</label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={currentMood}
                  onChange={(e) => setCurrentMood(parseInt(e.target.value))}
                  className="mood-slider"
                />
                <div className="mood-value">{currentMood}</div>
              </div>
              
              <div className="emotions-selector">
                <label>Эмоции:</label>
                <StickerSelector
                  onStickerSelect={(sticker) => {
                    if (!currentEmotions.includes(sticker)) {
                      setCurrentEmotions([...currentEmotions, sticker]);
                    }
                  }}
                  category="emotions"
                  title="Выбери эмоции"
                />
                <div className="selected-emotions">
                  {currentEmotions.map((emotion, index) => (
                    <span key={index} className="emotion-tag">
                      {emotion}
                      <button onClick={() => setCurrentEmotions(currentEmotions.filter((_, i) => i !== index))}>×</button>
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="activities-selector">
                <label>Активности:</label>
                <StickerSelector
                  onStickerSelect={(sticker) => {
                    if (!currentActivities.includes(sticker)) {
                      setCurrentActivities([...currentActivities, sticker]);
                    }
                  }}
                  category="activities"
                  title="Что делала?"
                />
                <div className="selected-activities">
                  {currentActivities.map((activity, index) => (
                    <span key={index} className="activity-tag">
                      {activity}
                      <button onClick={() => setCurrentActivities(currentActivities.filter((_, i) => i !== index))}>×</button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowMoodModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={saveMoodEntry} className="btn btn-primary">Сохранить</button>
            </div>
          </div>
        </div>
      )}

      {/* Gratitude Modal */}
      {showGratitudeModal && (
        <div className="modal-overlay" onClick={() => setShowGratitudeModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>За что ты благодарна сегодня?</h3>
              <button onClick={() => setShowGratitudeModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <GratitudeForm onSave={saveGratitudeEntry} />
            </div>
          </div>
        </div>
      )}

      {/* Stress Modal */}
      {showStressModal && (
        <div className="modal-overlay" onClick={() => setShowStressModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Уровень стресса</h3>
              <button onClick={() => setShowStressModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <StressForm onSave={saveStressEntry} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Gratitude Form Component
const GratitudeForm: React.FC<{ onSave: (items: string[], reflection: string) => void }> = ({ onSave }) => {
  const [items, setItems] = useState(['']);
  const [reflection, setReflection] = useState('');

  const addItem = () => {
    setItems([...items, '']);
  };

  const updateItem = (index: number, value: string) => {
    const newItems = [...items];
    newItems[index] = value;
    setItems(newItems);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    const validItems = items.filter(item => item.trim());
    if (validItems.length > 0) {
      onSave(validItems, reflection);
    }
  };

  return (
    <>
      <div className="gratitude-items-input">
        <label>За что благодарна:</label>
        {items.map((item, index) => (
          <div key={index} className="gratitude-item-input">
            <input
              type="text"
              value={item}
              onChange={(e) => updateItem(index, e.target.value)}
              placeholder={`Благодарность ${index + 1}`}
              className="modal-input"
            />
            {items.length > 1 && (
              <button onClick={() => removeItem(index)} className="remove-btn">×</button>
            )}
          </div>
        ))}
        <button onClick={addItem} className="btn btn-secondary">Добавить еще</button>
      </div>
      
      <div className="gratitude-reflection-input">
        <label>Размышления:</label>
        <textarea
          value={reflection}
          onChange={(e) => setReflection(e.target.value)}
          placeholder="Как эти моменты благодарности повлияли на твой день?"
          className="modal-textarea"
          rows={3}
        />
      </div>
      
      <div className="modal-footer">
        <button onClick={handleSave} className="btn btn-primary">Сохранить</button>
      </div>
    </>
  );
};

// Stress Form Component
const StressForm: React.FC<{ onSave: (level: number, triggers: string[], copingMethods: string[], effectiveness: number) => void }> = ({ onSave }) => {
  const [level, setLevel] = useState(5);
  const [triggers, setTriggers] = useState(['']);
  const [copingMethods, setCopingMethods] = useState(['']);
  const [effectiveness, setEffectiveness] = useState(3);

  const addTrigger = () => {
    setTriggers([...triggers, '']);
  };

  const updateTrigger = (index: number, value: string) => {
    const newTriggers = [...triggers];
    newTriggers[index] = value;
    setTriggers(newTriggers);
  };

  const removeTrigger = (index: number) => {
    setTriggers(triggers.filter((_, i) => i !== index));
  };

  const addCopingMethod = () => {
    setCopingMethods([...copingMethods, '']);
  };

  const updateCopingMethod = (index: number, value: string) => {
    const newMethods = [...copingMethods];
    newMethods[index] = value;
    setCopingMethods(newMethods);
  };

  const removeCopingMethod = (index: number) => {
    setCopingMethods(copingMethods.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    const validTriggers = triggers.filter(trigger => trigger.trim());
    const validMethods = copingMethods.filter(method => method.trim());
    onSave(level, validTriggers, validMethods, effectiveness);
  };

  return (
    <>
      <div className="stress-level-input">
        <label>Уровень стресса (1-10):</label>
        <input
          type="range"
          min="1"
          max="10"
          value={level}
          onChange={(e) => setLevel(parseInt(e.target.value))}
          className="stress-slider"
        />
        <div className="stress-value">{level}</div>
      </div>
      
      <div className="stress-triggers-input">
        <label>Причины стресса:</label>
        {triggers.map((trigger, index) => (
          <div key={index} className="stress-item-input">
            <input
              type="text"
              value={trigger}
              onChange={(e) => updateTrigger(index, e.target.value)}
              placeholder={`Причина ${index + 1}`}
              className="modal-input"
            />
            {triggers.length > 1 && (
              <button onClick={() => removeTrigger(index)} className="remove-btn">×</button>
            )}
          </div>
        ))}
        <button onClick={addTrigger} className="btn btn-secondary">Добавить причину</button>
      </div>
      
      <div className="stress-coping-input">
        <label>Методы борьбы:</label>
        {copingMethods.map((method, index) => (
          <div key={index} className="stress-item-input">
            <input
              type="text"
              value={method}
              onChange={(e) => updateCopingMethod(index, e.target.value)}
              placeholder={`Метод ${index + 1}`}
              className="modal-input"
            />
            {copingMethods.length > 1 && (
              <button onClick={() => removeCopingMethod(index)} className="remove-btn">×</button>
            )}
          </div>
        ))}
        <button onClick={addCopingMethod} className="btn btn-secondary">Добавить метод</button>
      </div>
      
      <div className="stress-effectiveness-input">
        <label>Эффективность методов (1-5):</label>
        <input
          type="range"
          min="1"
          max="5"
          value={effectiveness}
          onChange={(e) => setEffectiveness(parseInt(e.target.value))}
          className="effectiveness-slider"
        />
        <div className="effectiveness-value">{effectiveness}</div>
      </div>
      
      <div className="modal-footer">
        <button onClick={handleSave} className="btn btn-primary">Сохранить</button>
      </div>
    </>
  );
};

// Vision Board Modal
const VisionBoardModal: React.FC<{ 
  isOpen: boolean; 
  onClose: () => void; 
  onUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  newItem: { caption: string; category: string };
  setNewItem: (item: { caption: string; category: string }) => void;
}> = ({ isOpen, onClose, onUpload, newItem, setNewItem }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Добавить изображение</h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        <div className="modal-body">
          <input
            type="text"
            placeholder="Подпись к изображению"
            value={newItem.caption}
            onChange={(e) => setNewItem({...newItem, caption: e.target.value})}
            className="modal-input"
          />
          <input
            type="text"
            placeholder="Категория"
            value={newItem.category}
            onChange={(e) => setNewItem({...newItem, category: e.target.value})}
            className="modal-input"
          />
          <div className="file-upload-section">
            <label className="file-upload-label">
              <input
                type="file"
                accept="image/*"
                onChange={onUpload}
                className="file-input"
              />
              <div className="file-upload-button">
                <span className="upload-icon">📷</span>
                <span>Выберите изображение</span>
              </div>
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">Отмена</button>
        </div>
      </div>
    </div>
  );
};

export default WellnessPage;
